import { useSyncExternalStore } from "react";

// Новая сборка фронтенда — без Ctrl+Shift+R.
//
// HTTP-кэш тут ни при чём: index.html отдаётся с no-cache, файлы сборки — с
// хэшем в имени. Старая версия живёт в УЖЕ ОТКРЫТОЙ вкладке и в установленном
// приложении на телефоне, которое неделями не перезапускается, а только
// выходит из фона. К тому же выкладка (rsync --delete) удаляет старые файлы, и
// такая вкладка, догружая кусок кода (страницу, карточку кандидата, мобильную
// версию), упала бы — см. chunkFailed.
//
// Поэтому страница сама сверяет свою сборку с той, что на сервере: раз в пару
// минут, при возврате во вкладку / из фона и когда вернулась сеть. Новая есть —
// перезагружается сама, если это ничего не ломает: страница скрыта (или только
// что из фона вернулась), нет звонка, отправок и загрузок в пути, ничего не
// набрано и не открыто окно. Иначе — плашка «Обновить», и попытка ещё раз при
// следующем уходе из вкладки.

const CHECK_EVERY_MS = 2 * 60_000;
// Не чаще этого на возвратах во вкладку: переключаются часто.
const MIN_GAP_MS = 20_000;
const RELOAD_MARK = "newats.update.reloadedAt";

/** Имя главного файла сборки из index.html: `/assets/index-<хэш>.js`. null —
 *  не сборка (vite dev отдаёт /src/main.tsx) или разметка не та. */
export function buildFromHtml(html: string): string | null {
  const m = /<script[^>]+type="module"[^>]+src="(\/assets\/index-[^"]+\.js)"/.exec(html);
  return m ? m[1] : null;
}

function runningBuild(): string | null {
  const el = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/index-"]');
  if (!el) return null;
  try {
    return new URL(el.src, location.href).pathname;
  } catch {
    return null;
  }
}

// ---- что держит перезагрузку ----

// Держатель → вопрос перед перезагрузкой по кнопке (reloadForUpdate).
const holds = new Map<symbol, string | undefined>();

/** Пока держат — сама страница не перезагрузится (отправка, загрузка,
 *  звонок). `ask` — о чём спросить, прежде чем перезагрузиться по кнопке
 *  «Обновить», для того, что браузер сам не защитит: отправку и загрузку
 *  стережёт их beforeunload («Покинуть сайт?»), а звонок оборвался бы молча.
 *  Возвращает «отпустить»; повторный вызов безвреден. */
export function holdReload(ask?: string): () => void {
  const h = Symbol("hold");
  holds.set(h, ask);
  return () => {
    if (holds.delete(h)) maybeReload("idle");
  };
}

/** Вопрос перед перезагрузкой по кнопке; null — спрашивать не о чем. */
export function reloadQuestion(): string | null {
  for (const ask of holds.values()) if (ask) return ask;
  return null;
}

// Набранное и открытые окна — всё, что пропало бы при перезагрузке. Поиск по
// чатам и фильтры живут в адресе, черновики переписки — в sessionStorage.
const TYPED = "textarea, [role=dialog] input, .modal input, .dr input, .lpr-field, .sched-menu input";
const OPEN = ".modal-backdrop, .m-sheet-backdrop, .att-lightbox, .ctxmenu, .sched-menu, .dr-root";

export function userBusy(doc: Pick<Document, "querySelectorAll" | "querySelector"> = document): boolean {
  if (doc.querySelector(OPEN)) return true;
  for (const el of doc.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(TYPED)) {
    if (el.type === "checkbox" || el.type === "radio" || el.type === "hidden") continue;
    if (el.value.trim()) return true;
  }
  return false;
}

/** Перезагружаться ли самим: новая сборка есть, ничего не держит, и
 *  пользователь сейчас не смотрит на страницу (или только что вернулся). */
export function shouldReload(o: {
  newer: boolean;
  held: boolean;
  busy: boolean;
  moment: "hidden" | "resumed" | "idle" | "visible";
}): boolean {
  if (!o.newer || o.held || o.busy) return false;
  return o.moment === "hidden" || o.moment === "resumed";
}

// ---- кусок сборки не догрузился ----
//
// Страницы, карточка кандидата, мобильная версия и JsSIP — отдельные куски
// сборки, их догружают при первом обращении. Вкладка, открытая до выкладки,
// просит кусок прошлой сборки, а его уже нет: rsync --delete в deploy:newats
// стёр, nginx на /assets/ отвечает 404 (try_files =404). Уже загруженные куски
// у вкладки в памяти — ломается только то, куда она ещё не заходила.

// Тексты ошибок import(): Chrome, Firefox, Safari; последнее — от vite, когда
// не пришли стили куска.
const CHUNK_ERROR_RE =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/i;

/** Ошибка загрузки куска сборки — а не ошибка в его коде: та перезагрузкой
 *  не лечится. */
export function isChunkLoadError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : typeof err === "string" ? err : "";
  return CHUNK_ERROR_RE.test(msg);
}

// Перезагрузились меньше этого назад, а кусок опять не пришёл — дело не в
// старой вкладке, а в сервере (выкладка на полпути): по кругу не грузимся. Или
// перезагрузку только что отменили на «Покинуть сайт?» (reloadNow) — тогда
// не переспрашиваем на каждом куске.
const RELOAD_LOOP_MS = 30_000;

/** Что на сервере по сравнению с этой вкладкой — по свежему index.html (`html`,
 *  null — не ответил) и сборке вкладки (`mine`):
 *  - `changed`     — другая сборка: была выкладка, кусков этой вкладки там нет;
 *  - `same`        — та же: кусок не пришёл по другой причине (сбой на одном
 *                    запросе, выкладка на полпути);
 *  - `unreachable` — не ответил или ответил не нашей страницей (ошибка прокси,
 *                    страница входа в Wi-Fi). */
export type ServerBuild = "changed" | "same" | "unreachable";

export function compareBuild(html: string | null, mine: string | null): ServerBuild {
  if (html === null) return "unreachable";
  const build = buildFromHtml(html);
  // Обе null — vite dev: сборки нет ни там, ни тут.
  if (build === mine) return "same";
  return build ? "changed" : "unreachable";
}

/** Что делать, когда кусок не догрузился:
 *  - `reload` — перезагрузиться сейчас;
 *  - `defer`  — перезагрузиться позже: на сервере новая сборка, но идёт звонок,
 *               отправка или загрузка, что-то набрано или открыто окно. Дальше —
 *               как с любой новой версией: плашка «Обновить» и перезагрузка при
 *               уходе из вкладки;
 *  - `fail`   — не перезагружаться.
 *  Не перезагрузились — ошибку показывает тот, кто грузил кусок. */
export function chunkErrorVerdict(o: {
  now: number;
  reloadedAt: number;
  online: boolean;
  server: ServerBuild;
  held: boolean;
  busy: boolean;
}): "reload" | "defer" | "fail" {
  // abs — часы, переведённые назад, не запрещают перезагрузку навсегда.
  if (Math.abs(o.now - o.reloadedAt) < RELOAD_LOOP_MS) return "fail";
  // Вместо приложения, которое пока работает, была бы страница браузера «Нет
  // подключения». navigator.onLine тут мало: при отвалившемся VPN или nginx на
  // перезапуске он по-прежнему true — поэтому и спрашиваем сервер.
  if (!o.online || o.server === "unreachable") return "fail";
  // Та же сборка — ждать нечего: новой версии, в которую перезагрузиться
  // потом, нет, а плашка «Вышла новая версия» соврала бы.
  if (o.held || o.busy) return o.server === "changed" ? "defer" : "fail";
  // И при той же сборке: сервер только что ответил, а лечит только
  // перезагрузка — неудачный import() браузер запоминает (Chromium: повторный
  // import() того же адреса — та же ошибка, и ни одного запроса на сервер).
  return "reload";
}

/** Что сказать пользователю, раз страница не перезагрузилась (ChunkFailed). */
export type ChunkCause = "offline" | "update" | "retry";

// Причина — по самой ошибке: роутер и React.lazy отдают дальше тот же объект.
const causes = new WeakMap<object, ChunkCause>();

export function chunkFailureCause(err: unknown): ChunkCause | undefined {
  return err !== null && typeof err === "object" ? causes.get(err) : undefined;
}

/** Попросил ли кто-то в этом beforeunload спросить «Покинуть сайт?» — так
 *  делают охранники отправок и загрузок (Conversation, UploadManager):
 *  preventDefault() или непустой returnValue. */
export function unloadAsked(e: Pick<BeforeUnloadEvent, "defaultPrevented" | "returnValue">): boolean {
  return e.defaultPrevented || !!e.returnValue;
}

// ---- состояние ----

let latest: string | null = null;
let running: string | null = null;
let lastCheck = 0;
// Перезагрузка уже идёт: остальные недогрузившиеся куски ждут её молча
// (onChunkError), а не показывают ошибку на долю секунды до неё.
let reloading = false;
// Кто ждёт. Не случилась перезагрузка — показать им их ошибку, а не висеть.
const waiting = new Set<() => void>();
let unloadWatch: ((e: BeforeUnloadEvent) => void) | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

const newer = () => !!running && !!latest && latest !== running;

/** Запомнить сборку с сервера; другая — плашка «Обновить» (useUpdateAvailable). */
function learnBuild(build: string | null) {
  if (!build || build === latest) return;
  latest = build;
  notify();
}

// Дольше не ждём: пока идёт проверка, висит и переход роутера.
const PROBE_MS = 10_000;

/** Свежий index.html; null — сервер не ответил или ответил ошибкой. */
async function fetchIndex(): Promise<string | null> {
  const ctl = new AbortController();
  const timer = window.setTimeout(() => ctl.abort(), PROBE_MS);
  try {
    const res = await fetch("/index.html", { cache: "no-store", credentials: "same-origin", signal: ctl.signal });
    return res.ok ? await res.text() : null;
  } catch {
    return null;
  } finally {
    window.clearTimeout(timer);
  }
}

function reloadedAt(): number {
  try {
    return Number(sessionStorage.getItem(RELOAD_MARK) || 0);
  } catch {
    return 0;
  }
}

/** `to` — куда шёл переход роутера, чей кусок не догрузился. Адрес в строке
 *  до конца перехода прежний, и простая перезагрузка вернула бы на ту же
 *  страницу: нажал «Чаты» — мигнуло, а на экране по-прежнему «Вакансии». */
function reloadNow(to?: string) {
  try {
    sessionStorage.setItem(RELOAD_MARK, String(Date.now()));
  } catch {
    /* без хранилища — просто перезагрузка */
  }
  reloading = true;
  // Охранник отправки или загрузки попросил браузер спросить «Покинуть
  // сайт?», и пользователь мог остаться. Тогда страница живёт дальше, а
  // защёлка `reloading` глушила бы всё до закрытия вкладки: каждый следующий
  // недогрузившийся кусок ждал бы перезагрузки, которой не будет, — «Чаты» по
  // клику не открываются, карточка вечно «Загрузка…», ни ошибки, ни
  // «Обновить». Ответа страница не узнаёт — отпускаем, раз спросили: ушёл
  // пользователь — страница и так сейчас уйдёт.
  //
  // Метку не снимаем: остался — значит, отказался уходить, и RELOAD_LOOP_MS
  // куски не просятся в перезагрузку сами, а показывают «Обновить». Иначе
  // каждый следующий спросил бы снова: на один клик по «Чатам» — два вопроса
  // подряд (роутер и vite:preloadError).
  //
  // Слушатель добавлен последним — видит решение всех охранников (Chromium
  // шлёт beforeunload прямо из location.reload()). Живёт до следующей
  // перезагрузки — на случай браузера, который спросит не сразу.
  if (unloadWatch) window.removeEventListener("beforeunload", unloadWatch);
  unloadWatch = (e) => {
    if (!reloading || !unloadAsked(e)) return;
    reloading = false;
    for (const fail of waiting) fail();
    waiting.clear();
  };
  window.addEventListener("beforeunload", unloadWatch);
  if (to && to !== location.pathname + location.search + location.hash) history.pushState(null, "", to);
  location.reload();
}

/** Как перезагружаться из-за куска новой страницы роутера: `to` — её адрес.
 *  С прежней страницы пользователь сам уходит, набранное там пропадёт и без
 *  перезагрузки — поэтому занятость (userBusy) тут не в счёт. */
export type ChunkNavigation = { leaving: true; to?: string };

// Один index.html на всех: роутер и vite:preloadError спрашивают про один и
// тот же кусок почти одновременно.
let probe: Promise<string | null> | null = null;

/** Кусок сборки не догрузился: спросить сервер, решает chunkErrorVerdict.
 *  true — страница уже перезагружается. */
export async function chunkFailed(err: unknown, nav?: ChunkNavigation): Promise<boolean> {
  if (reloading) return true;
  if (!isChunkLoadError(err)) return false;
  const online = typeof navigator === "undefined" || navigator.onLine !== false;
  let html: string | null = null;
  if (online) {
    probe ??= fetchIndex().finally(() => {
      probe = null;
    });
    html = await probe;
    // Пока ждали сервер, перезагрузку начал другой кусок.
    if (reloading) return true;
  }
  const server = compareBuild(html, running ?? runningBuild());
  const verdict = chunkErrorVerdict({
    now: Date.now(),
    reloadedAt: reloadedAt(),
    online,
    server,
    held: holds.size > 0,
    busy: !nav && userBusy(),
  });
  if (err !== null && typeof err === "object") {
    causes.set(err, !online || server === "unreachable" ? "offline" : server === "changed" ? "update" : "retry");
  }
  if (verdict === "reload") {
    reloadNow(nav?.to);
    // Иначе браузер спросил «Покинуть сайт?», и reloadNow всё уже отпустил.
    if (reloading) return true;
  }
  if (server === "changed" && html !== null) learnBuild(buildFromHtml(html));
  return false;
}

/** Для `.catch` у import(): страница перезагружается — промис не разрешится,
 *  и на экране до перезагрузки остаётся то, что было (а не экран ошибки на
 *  долю секунды); иначе — та же ошибка дальше, к тому, кто покажет
 *  «Обновить». Перезагрузку отменили («Покинуть сайт?» → остаться) — тоже
 *  ошибка дальше. */
export function onChunkError(err: unknown, nav?: ChunkNavigation): Promise<never> {
  return chunkFailed(err, nav).then((reloads) =>
    reloads ? new Promise<never>((_, reject) => waiting.add(() => reject(err))) : Promise.reject(err),
  );
}

function maybeReload(moment: "hidden" | "resumed" | "idle" | "visible") {
  // Первым делом: без новой сборки смотреть на остальное незачем.
  if (!newer()) return;
  const m = moment === "idle" ? (document.visibilityState === "hidden" ? "hidden" : "visible") : moment;
  if (shouldReload({ newer: true, held: holds.size > 0, busy: userBusy(), moment: m })) reloadNow();
}

async function check(moment: "hidden" | "resumed" | "visible") {
  if (!running) return;
  const now = Date.now();
  if (now - lastCheck < MIN_GAP_MS && moment !== "resumed") return;
  lastCheck = now;
  const html = await fetchIndex();
  // null — нет сети: проверим, когда вернётся.
  const build = html === null ? null : buildFromHtml(html);
  if (!build) return;
  learnBuild(build);
  maybeReload(moment);
}

let started = false;

/** Запускается один раз из main.tsx. В разработке (vite dev) ничего не делает. */
export function startUpdateWatch() {
  if (started || typeof window === "undefined") return;
  started = true;
  // Кусок кода старой сборки не догрузился (выкладка его удалила) — это та
  // же новая версия (chunkFailed). Ошибку не глушим (preventDefault): с ней
  // import() вернул бы undefined, и React.lazy упал бы уже на нём. Её ждёт
  // тот, кто грузил кусок, — роутер, карточка, софтфон — и покажет своё.
  //
  // Событие приходит раньше, чем ошибка — к нему, а первое слово за ним:
  // роутер знает, куда шёл переход (reloadNow, to). Поэтому здесь — после, для
  // тех, кто chunkFailed не зовёт сам (JsSIP); повтор решения безвреден: сервер
  // оба спрашивают одним запросом, а начатая перезагрузка — одна на всех.
  window.addEventListener("vite:preloadError", (e) => {
    const err = (e as Event & { payload?: unknown }).payload;
    window.setTimeout(() => void chunkFailed(err), 0);
  });
  running = runningBuild();
  if (!running) return;
  let hiddenAt = 0;
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      hiddenAt = Date.now();
      // Ушёл из вкладки, а новая версия уже известна — самое время.
      maybeReload("hidden");
      return;
    }
    const away = hiddenAt ? Date.now() - hiddenAt : 0;
    hiddenAt = 0;
    void check(away > 5_000 ? "resumed" : "visible");
  });
  window.addEventListener("online", () => void check("visible"));
  window.setInterval(() => {
    void check(document.visibilityState === "hidden" ? "hidden" : "visible");
  }, CHECK_EVERY_MS);
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

/** Есть новая сборка, а страница ещё старая — показать «Обновить». */
export function useUpdateAvailable(): boolean {
  return useSyncExternalStore(subscribe, newer, () => false);
}

/** «Обновить» по кнопке: пользователь сам решил — ничего не ждём. Отправки и
 *  загрузки в пути браузер сам предложит не бросать (их beforeunload), а о
 *  том, чего он не защитит, — звонке — спрашиваем сами (holdReload, ask):
 *  «Обновить» есть и на плашке, и на листе недогрузившейся карточки, которую
 *  открывают прямо во время разговора. */
export function reloadForUpdate() {
  const ask = reloadQuestion();
  if (ask && !window.confirm(ask)) return;
  reloadNow();
}
